/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { AnyObject } from "../../../shared/types/app.type";
import { BulkUrlType } from "../../../shared/types/bulk-upload/bulk-url.type";
import {
  Asset,
  AssetAttachment,
  AssetAttribute,
  AssetCountByCoverageStatus,
  AssetModel,
  AssetOem,
  AssetOemModelType,
  AssetOemModelTypeRequest,
  AssetType,
  Attribute,
  CalculatorAssetModel,
  CalculatorAssetModelRequest,
  CalculatorAssetOem,
  CalculatorAssetOemRequest,
  CalculatorAssetType,
  CalculatorAssetTypeRequest,
  ContractAsset,
} from "./asset.type";

@Injectable({
  providedIn: "root",
})
export class AssetService {
  basePath = `${environment.serverUrl}/assets`;
  basePathOems = `${environment.serverUrl}/asset-oems`;
  basePathModels = `${environment.serverUrl}/asset-models`;
  basePathTypes = `${environment.serverUrl}/asset-types`;
  basePathCsvQueue = `${environment.serverUrl}/assets/queue-csv`;
  basePathContractAsset = `${environment.serverUrl}/contract-assets`;
  basePathContractAssetCsvQueue = `${environment.serverUrl}/contract-assets/queue-csv`;
  basePathAssetAttributes = `${environment.serverUrl}/asset-attributes`;
  basePathAttributes = `${environment.serverUrl}/attributes`;

  constructor(private http: HttpClient) {}

  //#region Asset

  getAsset(assetId: string, options?: QueryParams<Asset>): Observable<Asset> {
    let params: HttpParams = new HttpParams();
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<Asset>(`${this.basePath}/${assetId}`, { params }).pipe(
      tap((_data: Asset) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getAssets(options?: QueryParams<Asset>): Observable<PaginatedResponse<Asset>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<Asset>>(this.basePath, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getAssetsPost(options?: QueryParams<Asset>): Observable<PaginatedResponse<Asset>> {
    const body = { ...DEFAULT_QUERY_PARAMS, ...options };
    return this.http.post<PaginatedResponse<Asset>>(`${this.basePath}/get-many`, body).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  createAsset(data: AnyObject): Observable<Asset> {
    data = this.prepareData(data);
    return this.http.post<Asset>(this.basePath, data).pipe(
      tap((_data: Asset) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateAsset(assetId: string, data: AnyObject): Observable<Asset> {
    data = this.prepareData(data);
    return this.http.put<Asset>(`${this.basePath}/${assetId}`, data).pipe(
      tap((_data: Asset) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  decommissionAsset(assetId: string, data = {}): Observable<Asset> {
    return this.http.put<Asset>(`${this.basePath}/${assetId}/decommission`, data).pipe(
      tap((_data: any) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  unDecommissionAsset(assetId: string): Observable<Asset> {
    return this.http.delete(`${this.basePath}/${assetId}/decommission`).pipe(catchError(this.handleError<any>("")));
  }

  terminateContractAsset(contractAssetId: string, data: Partial<ContractAsset>): Observable<ContractAsset> {
    return this.http.put<ContractAsset>(`${this.basePathContractAsset}/${contractAssetId}/terminate`, data).pipe(
      tap((_data: ContractAsset) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  unterminateContractAsset(contractAssetId: string): Observable<ContractAsset> {
    return this.http.put<ContractAsset>(`${this.basePathContractAsset}/${contractAssetId}/unterminate`, {}).pipe(
      tap((_data: ContractAsset) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateContractAsset(contractAssetId: string, data: AnyObject): Observable<ContractAsset> {
    data = this.prepareData(data);
    return this.http.put<Asset>(`${this.basePathContractAsset}/${contractAssetId}`, data).pipe(
      tap((_data: Asset) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getCountByCoverageStatus() {
    return this.http.get<AssetCountByCoverageStatus>(`${this.basePath}/count-by-coverage-status`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getPutSignedUrl(): Observable<BulkUrlType> {
    return this.http.get<any>(`${this.basePath}/batchs/bulk-upload-url`).pipe(
      tap((_data: BulkUrlType) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  uploadBatch(url: string, csvFile: File) {
    return this.http.put<any>(url, csvFile);
  }

  actionRequest(data): Observable<Asset> {
    return this.http.post<any>(`${this.basePath}/actions/request`, data);
  }

  getAssetsQueue(options?: QueryParams<Asset>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getAssetOEM(oemId: string): Observable<CalculatorAssetOem> {
    return this.http.get<any>(`${this.basePathOems}/${oemId}`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getAssetOEMs(options?: QueryParams<CalculatorAssetOemRequest>): Observable<PaginatedResponse<CalculatorAssetOem>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<CalculatorAssetOem>>(`${this.basePathOems}/calculator-api`, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  createAssetOEM(data): Observable<AssetOem> {
    return this.http.post<AssetOem>(this.basePathOems, data);
  }

  getAssetModel(modelId: string): Observable<CalculatorAssetModel> {
    return this.http.get<any>(`${this.basePathModels}/${modelId}`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getAssetModels(
    options?: QueryParams<CalculatorAssetModelRequest>,
  ): Observable<PaginatedResponse<CalculatorAssetModel>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<any>>(`${this.basePathModels}/calculator-api`, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  createAssetModel(data): Observable<AssetModel> {
    return this.http.post<AssetModel>(this.basePathModels, data);
  }

  getAssetType(typeId: string): Observable<CalculatorAssetType> {
    return this.http.get<any>(`${this.basePathTypes}/${typeId}`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getAssetTypes(options?: QueryParams<CalculatorAssetTypeRequest>): Observable<PaginatedResponse<CalculatorAssetType>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<any>>(`${this.basePathTypes}/calculator-api`, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  createAssetType(data): Observable<AssetType> {
    return this.http.post<AssetType>(this.basePathTypes, data);
  }

  createAssetOemModelType(data: AssetOemModelTypeRequest): Observable<AssetOemModelType> {
    return this.http.post<AssetOemModelType>(`${this.basePath}/equipment-calculator-api`, data);
  }
  //assets/equipment-calculator-api

  // #endregion

  //#region ContractAsset

  getContractAssets(options?: QueryParams<ContractAsset>): Observable<PaginatedResponse<ContractAsset>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<ContractAsset>>(this.basePathContractAsset, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getContractAssetsQueue(options?: QueryParams<ContractAsset>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathContractAssetCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  // #endregion

  //#region Attachment Tab
  getAssetAttachments(
    assetId: string,
    options?: QueryParams<AssetAttachment>,
  ): Observable<PaginatedResponse<AssetAttachment>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http
      .get<PaginatedResponse<AssetAttachment>>(`${this.basePath}/${assetId}/asset-attachments`, {
        params,
      })
      .pipe(catchError(this.handleError<any>()));
  }

  updateAssetAttachment(
    assetId: string,
    assetAttachmentId: string,
    data: Partial<AssetAttachment>,
  ): Observable<AssetAttachment> {
    return this.http
      .put<Asset>(`${this.basePath}/${assetId}/asset-attachments/${assetAttachmentId}`, data)
      .pipe(catchError(this.handleError<any>()));
  }

  createAttachment(
    asset: Pick<AssetAttachment, "name" | "url" | "description" | "assetId">,
  ): Observable<AssetAttachment> {
    return this.http
      .post<Observable<AssetAttachment>>(`${this.basePath}/${asset.assetId}/asset-attachments`, asset)
      .pipe(catchError(this.handleError<any>()));
  }

  getS3Url(assetId: string, extension: string): Observable<{ url: string; name: string }> {
    return this.http
      .get<Asset>(`${this.basePath}/${assetId}/url-asset-attachments`, { params: { extension } })
      .pipe(catchError(this.handleError<any>()));
  }

  //#endregion

  //#region Attributes Tab
  getAssetAttributes(
    assetId: string,
    options?: QueryParams<AssetAttribute>,
  ): Observable<PaginatedResponse<AssetAttribute>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http
      .get<PaginatedResponse<AssetAttribute>>(`${this.basePathAssetAttributes}`, {
        params,
      })
      .pipe(catchError(this.handleError<any>()));
  }

  createAssetAttribute(
    assetAttribute: Pick<AssetAttribute, "quantity" | "assetId" | "attributeId" | "deleted">,
  ): Observable<AssetAttribute> {
    return this.http
      .post<Observable<AssetAttribute>>(`${this.basePathAssetAttributes}`, assetAttribute)
      .pipe(catchError(this.handleError<any>()));
  }

  updateAssetAttribute(assetAttributeId: string, data: Partial<AssetAttribute>): Observable<AssetAttribute> {
    return this.http
      .put<AssetAttribute>(`${this.basePathAssetAttributes}/${assetAttributeId}`, data)
      .pipe(catchError(this.handleError<any>()));
  }

  restoreAssetAttribute(assetAttributeId: string): Observable<AssetAttribute> {
    return this.http
      .put<AssetAttribute>(`${this.basePathAssetAttributes}/${assetAttributeId}/restore`, {})
      .pipe(catchError(this.handleError<any>()));
  }

  updateAttribute(attributeId: string, data: Partial<Attribute>): Observable<Attribute> {
    return this.http
      .put<Attribute>(`${this.basePathAttributes}/${attributeId}`, data)
      .pipe(catchError(this.handleError<any>()));
  }

  getAttributes(options?: QueryParams<Attribute>): Observable<PaginatedResponse<Attribute>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http
      .get<PaginatedResponse<Attribute>>(`${this.basePathAttributes}`, {
        params,
      })
      .pipe(catchError(this.handleError<any>()));
  }

  createAttribute(data: Partial<Attribute>): Observable<Attribute> {
    return this.http.post<Attribute>(`${this.basePathAttributes}`, data);
  }

  // #region Private methods

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return throwError(error);
    };
  }

  private prepareData(data: Asset): Asset {
    data = { ...data, ...this.getParsedPrices(data) };
    const preparedData = {};
    for (const key of Object.keys(data)) {
      const value = data[key];
      // check if has value
      if (value !== undefined) {
        // apply type conversion
        if (key === "partnerPrice" || key === "clientPrice" || key === "channelWorksCost") {
          preparedData[key] = Number(value);
        }
        preparedData[key] = value;
      }
    }
    return preparedData;
  }

  private getParsedPrices(data: Asset): Asset {
    let { partnerPrice, clientPrice } = data;
    if (typeof partnerPrice === "string") {
      partnerPrice = Number(partnerPrice);
    }
    if (typeof clientPrice === "string") {
      clientPrice = Number(clientPrice);
    }
    return { partnerPrice, clientPrice };
  }

  // #endregion
}
